import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { Button } from '../../components/Button'
import { CheckboxField, Field } from '../../components/Field'
import { checkGhanaPhone } from '../../lib/ghanaPhone'
import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  ADDRESS_MAX,
  EMERGENCY_INFO_KEY,
  GHANA_LAT,
  GHANA_LNG,
  HOURS_MAX,
  fetchEmergencyInfo,
  updateEmergencyInfo,
  type EmergencyInfo,
  type EmergencyInfoUpdate,
} from '../api/emergencyInfo'
import { Callout } from '../components/Callout'
import { DataTable, type Column } from '../components/DataTable'
import { DetailDrawer } from '../components/DetailDrawer'
import { StatusPill } from '../components/StatusPill'

/**
 * Emergency & contact details (M35-T05, ADR-0064). amtc_admin only.
 *
 * Where AMTC staff correct each partner's emergency line, address, hours and
 * map pin, and mark them as checked. Emergency Help shows a patient those
 * details ONLY once they are marked verified (and the old /emergency flow's
 * Call ER button obeys the same rule), so this page is what turns placeholder
 * data into something a person in an emergency can use.
 *
 * Two checks, kept apart as the server keeps them: "emergency info" (line,
 * address, hours, emergency dept, 24-hour) and "location" (the pin). Saving a
 * changed value clears its group's check, because a changed number is not a
 * checked number; saving unchanged values clears nothing.
 *
 * NOT `hospitals.verified`: that is the public vetted-partner badge. Every
 * label here says "emergency info" or "location" so the two cannot be confused.
 */
export function EmergencyContactsPage(): ReactNode {
  const { token } = useAuth()
  const [openSlug, setOpenSlug] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: EMERGENCY_INFO_KEY,
    queryFn: () => fetchEmergencyInfo(token),
    enabled: Boolean(token),
  })
  const rows = query.data ?? []
  const open = rows.find((r) => r.slug === openSlug) ?? null

  // Stable, or DetailDrawer's effect re-runs on every render and moves focus
  // to its Close button after each save.
  const closeDrawer = useCallback(() => setOpenSlug(null), [])

  const columns: readonly Column<EmergencyInfo>[] = [
    {
      key: 'name',
      header: 'Hospital',
      // A real button, so a keyboard user can open a hospital: the row click
      // is mouse-only.
      cell: (r) => (
        <button
          type="button"
          onClick={() => setOpenSlug(r.slug)}
          className="text-left font-medium text-ink-1 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {r.name}
        </button>
      ),
    },
    {
      key: 'info',
      header: 'Emergency info',
      cell: (r) =>
        r.emergency_info_verified_at ? (
          <span className="inline-flex flex-col gap-0.5">
            <StatusPill
              status="verified"
              tone="done"
              label="Emergency info verified"
            />
            <span className="text-xs text-ink-3">
              {formatDate(r.emergency_info_verified_at)}
              {r.emergency_info_verified_by_name
                ? ` · ${r.emergency_info_verified_by_name}`
                : ''}
            </span>
          </span>
        ) : (
          <StatusPill
            status="unverified"
            tone="attention"
            label="Not verified"
          />
        ),
    },
    {
      key: 'location',
      header: 'Location',
      cell: (r) =>
        r.location_verified ? (
          <StatusPill status="verified" tone="done" label="Location verified" />
        ) : (
          <StatusPill
            status="unverified"
            tone="attention"
            label="Not verified"
          />
        ),
    },
  ]

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Emergency &amp; contact details
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Check each hospital&apos;s emergency line, address, hours and map pin
          with the hospital, then mark them as verified.
        </p>
      </div>
      <Callout tone="info">
        Patients see this hospital&apos;s emergency details only when its
        emergency info is marked verified. Address and map position also appear
        on the public hospital profile as soon as you save.
      </Callout>

      <DataTable
        rows={rows}
        columns={columns}
        getKey={(r) => r.slug}
        searchText={(r) => r.name}
        caption="Partner hospitals and whether their emergency details are verified"
        isPending={query.isPending}
        isError={query.isError}
        errorMessage="Could not load hospitals."
        onRetry={() => void query.refetch()}
        onRowClick={(r) => setOpenSlug(r.slug)}
      />

      {open && (
        <EditDrawer
          key={open.slug}
          info={open}
          onClose={closeDrawer}
          onSave={(update) => updateEmergencyInfo(token, open.slug, update)}
          onSaved={() =>
            void queryClient.invalidateQueries({ queryKey: EMERGENCY_INFO_KEY })
          }
        />
      )}
    </section>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

type FieldName =
  | 'address'
  | 'emergency_phone'
  | 'emergency_hours_text'
  | 'lat'
  | 'lng'
type FieldErrors = Partial<Record<FieldName, string>>
const FIELD_NAMES: readonly FieldName[] = [
  'address',
  'emergency_phone',
  'emergency_hours_text',
  'lat',
  'lng',
]

/** The server stores coordinates as Numeric(9,6), so compare at that precision. */
function samePosition(a: number, b: number): boolean {
  return Math.round(a * 1e6) === Math.round(b * 1e6)
}

/** A 400 from the server, mapped onto the fields it names where it can be. */
function serverFieldErrors(err: unknown): FieldErrors {
  if (!(err instanceof ApiError)) return {}
  if (err.code === 'invalid_phone') return { emergency_phone: err.message }
  const out: FieldErrors = {}
  const fields = err.details.fields
  if (fields && typeof fields === 'object') {
    for (const [loc, msg] of Object.entries(
      fields as Record<string, unknown>,
    )) {
      const name = loc.replace(/^body\./, '') as FieldName
      if (FIELD_NAMES.includes(name) && typeof msg === 'string') {
        out[name] = msg.replace(/^Value error, /, '')
      }
    }
  }
  return out
}

function EditDrawer({
  info,
  onClose,
  onSave,
  onSaved,
}: {
  info: EmergencyInfo
  onClose: () => void
  onSave: (update: EmergencyInfoUpdate) => Promise<EmergencyInfo>
  onSaved: () => void
}): ReactNode {
  // What the server last told us is stored. Every "changed?" question is asked
  // against this, and it is replaced by each save's response.
  const [stored, setStored] = useState(info)
  const [address, setAddress] = useState(info.address)
  const [phone, setPhone] = useState(info.emergency_phone ?? '')
  const [hours, setHours] = useState(info.emergency_hours_text ?? '')
  // Tri-state: null means no one has recorded it. Shown unticked, and sent
  // only once someone sets it, so "unknown" never becomes "no".
  const [hasEmergency, setHasEmergency] = useState(info.has_emergency)
  const [is24h, setIs24h] = useState(info.emergency_24h)
  const [lat, setLat] = useState(String(info.lat))
  const [lng, setLng] = useState(String(info.lng))
  // The location check is sent only when someone ticks or unticks it. Left
  // alone, the server decides: a moved pin clears it, an unmoved one keeps it.
  const [locationTicked, setLocationTicked] = useState<boolean | null>(null)
  const [touched, setTouched] = useState<ReadonlySet<FieldName>>(new Set())
  const [serverErrors, setServerErrors] = useState<FieldErrors>({})
  const [savedAt, setSavedAt] = useState<string | null>(null)

  useEffect(() => {
    if (!savedAt) return
    const t = setTimeout(() => setSavedAt(null), 3000)
    return () => clearTimeout(t)
  }, [savedAt])

  const latNum = Number(lat)
  const lngNum = Number(lng)
  const pinMoved =
    lat.trim() !== '' &&
    lng.trim() !== '' &&
    !(samePosition(latNum, stored.lat) && samePosition(lngNum, stored.lng))
  const locationVerified =
    locationTicked ?? (pinMoved ? false : stored.location_verified)

  // A value left as it was stored is not re-checked: the server treats it as
  // unchanged, whatever format it was saved in. Anything new must be a real
  // Ghana number.
  const phoneChanged = phone.trim() !== (stored.emergency_phone ?? '')
  const phoneCheck =
    phoneChanged && phone.trim() !== '' ? checkGhanaPhone(phone) : null

  // The rest follow the server's schema. Its checks run on every value sent,
  // changed or not, so a stored value that fails them (legacy data) is checked
  // here only once edited, and left out of the request until then; otherwise
  // it would block every save of the hospital.
  const addressChanged = address.trim() !== stored.address
  const hoursChanged = hours.trim() !== (stored.emergency_hours_text ?? '')
  const latChanged = !samePosition(latNum, stored.lat) || lat.trim() === ''
  const lngChanged = !samePosition(lngNum, stored.lng) || lng.trim() === ''
  const ruleErrors: FieldErrors = {}
  if (address.trim() === '') ruleErrors.address = 'Enter the address.'
  else if (address.trim().length > ADDRESS_MAX)
    ruleErrors.address = `Keep the address to ${ADDRESS_MAX} characters.`
  if (hours.trim().length > HOURS_MAX)
    ruleErrors.emergency_hours_text = `Keep the hours to ${HOURS_MAX} characters.`
  if (lat.trim() === '' || Number.isNaN(latNum))
    ruleErrors.lat = 'Enter a latitude.'
  else if (latNum < GHANA_LAT[0] || latNum > GHANA_LAT[1])
    ruleErrors.lat = `Latitude must be within Ghana (${GHANA_LAT[0]} to ${GHANA_LAT[1]}).`
  if (lng.trim() === '' || Number.isNaN(lngNum))
    ruleErrors.lng = 'Enter a longitude.'
  else if (lngNum < GHANA_LNG[0] || lngNum > GHANA_LNG[1])
    ruleErrors.lng = `Longitude must be within Ghana (${GHANA_LNG[0]} to ${GHANA_LNG[1]}).`

  const clientErrors: FieldErrors = {}
  if (addressChanged && ruleErrors.address)
    clientErrors.address = ruleErrors.address
  if (phoneCheck && !phoneCheck.ok)
    clientErrors.emergency_phone = phoneCheck.message
  if (hoursChanged && ruleErrors.emergency_hours_text)
    clientErrors.emergency_hours_text = ruleErrors.emergency_hours_text
  if (latChanged && ruleErrors.lat) clientErrors.lat = ruleErrors.lat
  if (lngChanged && ruleErrors.lng) clientErrors.lng = ruleErrors.lng

  const errorFor = (
    f: FieldName,
  ): { error: string } | Record<string, never> => {
    const e = (touched.has(f) ? clientErrors[f] : undefined) ?? serverErrors[f]
    return e ? { error: e } : {}
  }
  const touch = (f: FieldName) => () =>
    setTouched((prev) => new Set(prev).add(f))
  const edit = (f: FieldName, set: (v: string) => void) => (v: string) => {
    set(v)
    setServerErrors((prev) => {
      if (!(f in prev)) return prev
      const next = { ...prev }
      delete next[f]
      return next
    })
  }

  // A ref, not `mutation.isPending`: a fast double click runs both handlers
  // before React re-renders, so the second still sees isPending false.
  const inFlight = useRef(false)
  const mutation = useMutation({
    mutationFn: (update: EmergencyInfoUpdate) => onSave(update),
    onSettled: () => {
      inFlight.current = false
    },
    onMutate: () => {
      setSavedAt(null)
      setServerErrors({})
    },
    onSuccess: (saved, update) => {
      setStored(saved)
      setSavedAt(new Date().toLocaleTimeString())
      onSaved()
      // "Mark not verified" sends the flag alone, so whatever is still being
      // edited in the form stays there, unsaved.
      if (
        Object.keys(update).length === 1 &&
        update.mark_info_verified === false
      )
        return
      setAddress(saved.address)
      setPhone(saved.emergency_phone ?? '')
      setHours(saved.emergency_hours_text ?? '')
      setHasEmergency(saved.has_emergency)
      setIs24h(saved.emergency_24h)
      setLat(String(saved.lat))
      setLng(String(saved.lng))
      setLocationTicked(null)
      setTouched(new Set())
    },
    onError: (err) => setServerErrors(serverFieldErrors(err)),
  })

  function send(update: EmergencyInfoUpdate) {
    if (inFlight.current) return
    inFlight.current = true
    mutation.mutate(update)
  }

  function save(mark: boolean) {
    if (Object.keys(clientErrors).length > 0) {
      setTouched(new Set(FIELD_NAMES))
      return
    }
    // Legacy values are left alone above, but they are never verified as they
    // stand: the server re-checks only what changed, so the check is here.
    const before = (what: string) => `Correct this before marking ${what}.`
    const blockers: FieldErrors = {}
    if (mark) {
      // Owner ruling 2026-10-23: no emergency number, no verified info. A
      // patient shown "verified" must have a line to call.
      if (phone.trim() === '')
        blockers.emergency_phone =
          'Add the emergency department phone before marking the info verified. If the hospital has no emergency line, leave it not verified.'
      else if (!checkGhanaPhone(phone).ok)
        blockers.emergency_phone = `This number is not a valid Ghana line. ${before('the info verified')}`
      if (ruleErrors.address)
        blockers.address = `${ruleErrors.address} ${before('the info verified')}`
      if (ruleErrors.emergency_hours_text)
        blockers.emergency_hours_text = `${ruleErrors.emergency_hours_text} ${before('the info verified')}`
    }
    if (locationTicked === true) {
      if (ruleErrors.lat)
        blockers.lat = `${ruleErrors.lat} ${before('the location verified')}`
      if (ruleErrors.lng)
        blockers.lng = `${ruleErrors.lng} ${before('the location verified')}`
    }
    if (Object.keys(blockers).length > 0) {
      setServerErrors(blockers)
      return
    }
    // Editable keys only: the server refuses anything else.
    const update: EmergencyInfoUpdate = {
      emergency_phone: phone.trim() === '' ? null : phone.trim(),
    }
    if (hoursChanged || !ruleErrors.emergency_hours_text)
      update.emergency_hours_text = hours.trim() === '' ? null : hours.trim()
    if (addressChanged || !ruleErrors.address) update.address = address.trim()
    if (latChanged || !ruleErrors.lat) update.lat = latNum
    if (lngChanged || !ruleErrors.lng) update.lng = lngNum
    if (hasEmergency !== null && hasEmergency !== stored.has_emergency)
      update.has_emergency = hasEmergency
    if (is24h !== null && is24h !== stored.emergency_24h)
      update.emergency_24h = is24h
    if (locationTicked !== null) update.location_verified = locationTicked
    if (mark) update.mark_info_verified = true
    send(update)
  }

  const fieldErrorShown = Object.keys(serverErrors).length > 0
  const serverError = !mutation.error
    ? null
    : fieldErrorShown
      ? 'Not saved. Check the fields marked above.'
      : mutation.error instanceof ApiError
        ? mutation.error.message
        : 'Save failed.'

  const notRecorded = (v: boolean | null) =>
    v === null ? <span className="text-ink-3"> (not recorded yet)</span> : null

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={info.name}
      footer={
        <div className="flex flex-wrap gap-2">
          {/* aria-disabled, not disabled, while saving: a browser blurs a
              focused button it disables, and focus would fall out of the
              drawer. The handlers ignore clicks while a save is pending. */}
          <Button
            kind="secondary"
            aria-disabled={mutation.isPending}
            onClick={() => save(false)}
          >
            Save
          </Button>
          <Button
            kind="primary"
            aria-disabled={mutation.isPending}
            onClick={() => save(true)}
          >
            Save and mark emergency info as verified today
          </Button>
          {stored.emergency_info_verified_at && (
            <Button
              kind="ghost"
              aria-disabled={mutation.isPending}
              // The flag alone: edits still in the form are not saved by it.
              onClick={() => send({ mark_info_verified: false })}
            >
              Mark emergency info as not verified
            </Button>
          )}
          <p role="status" className="w-full text-sm font-medium text-bed-good">
            {savedAt ? `Saved · ${savedAt}` : ''}
          </p>
          {serverError && (
            <p role="alert" className="w-full text-xs text-bed-bad">
              {serverError}
            </p>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-semibold text-ink-1">
            Emergency info
          </legend>
          <p className="text-xs text-ink-3">
            {stored.emergency_info_verified_at
              ? `Verified ${formatDate(stored.emergency_info_verified_at)}${
                  stored.emergency_info_verified_by_name
                    ? ` by ${stored.emergency_info_verified_by_name}`
                    : ''
                }. Changing any of these clears the check.`
              : 'Not verified. Patients do not see these details yet.'}
          </p>
          <Field
            label="Address"
            value={address}
            maxLength={ADDRESS_MAX}
            onChange={(e) => edit('address', setAddress)(e.target.value)}
            onBlur={touch('address')}
            readOnly={mutation.isPending}
            {...errorFor('address')}
          />
          <Field
            label="Emergency department phone"
            type="tel"
            value={phone}
            onChange={(e) => edit('emergency_phone', setPhone)(e.target.value)}
            onBlur={touch('emergency_phone')}
            readOnly={mutation.isPending}
            hint="The emergency line, not the switchboard. Leave empty if unknown."
            {...errorFor('emergency_phone')}
          />
          <Field
            label="Emergency hours"
            value={hours}
            maxLength={HOURS_MAX}
            onChange={(e) =>
              edit('emergency_hours_text', setHours)(e.target.value)
            }
            onBlur={touch('emergency_hours_text')}
            readOnly={mutation.isPending}
            hint='For example "24 hours" or "Mon–Fri 08:00–20:00".'
            {...errorFor('emergency_hours_text')}
          />
          <CheckboxField
            label={<>Has an emergency department{notRecorded(hasEmergency)}</>}
            checked={hasEmergency === true}
            onChange={setHasEmergency}
          />
          <CheckboxField
            label={<>Open 24 hours{notRecorded(is24h)}</>}
            checked={is24h === true}
            onChange={setIs24h}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-semibold text-ink-1">
            Location
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Latitude"
              type="number"
              value={lat}
              step="0.000001"
              onChange={(e) => {
                edit('lat', setLat)(e.target.value)
                setLocationTicked(null)
              }}
              onBlur={touch('lat')}
              readOnly={mutation.isPending}
              {...errorFor('lat')}
            />
            <Field
              label="Longitude"
              type="number"
              value={lng}
              step="0.000001"
              onChange={(e) => {
                edit('lng', setLng)(e.target.value)
                setLocationTicked(null)
              }}
              onBlur={touch('lng')}
              readOnly={mutation.isPending}
              {...errorFor('lng')}
            />
          </div>
          <CheckboxField
            label="Location verified (the pin is at the emergency entrance)"
            checked={locationVerified}
            onChange={setLocationTicked}
          />
          {pinMoved && stored.location_verified && locationTicked === null && (
            <p className="text-xs text-ink-3">
              Moving the pin clears this check. Tick it again once the new
              position is confirmed.
            </p>
          )}
        </fieldset>
      </div>
    </DetailDrawer>
  )
}
